import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type PagedResponse<TItem> = {
  data?: TItem[];
  hints?: {
    count?: number;
    offset?: number;
    limit?: number;
    hasMore?: boolean;
  };
};

export type UseFetchAllPagesOptions<
  TItem,
  TRequestParams extends Record<string, unknown>,
  TResponse extends PagedResponse<TItem>,
> = {
  /**
   * Fetches one page of results from the API using offset/limit pagination.
   */
  fetchPage: (params: TRequestParams & { offset: number; limit: number }) => Promise<TResponse>;
  /**
   * Base request params that should be merged into every paginated request.
   */
  baseParams: TRequestParams;
  /**
   * Number of records to fetch per page. Defaults to 100.
   */
  pageSize?: number;
  /**
   * Safety cap for max records to collect in client memory. Defaults to 2000.
   */
  maxRows?: number;
  /**
   * Enable/disable fetching without unmounting the caller. Defaults to true.
   */
  enabled?: boolean;
  /**
   * Optional extractor for records when API response shape differs from `{ data: [] }`.
   */
  getItems?: (response: TResponse) => TItem[];
  /**
   * Optional extractor for total count when API response shape differs from `hints.count`.
   */
  getTotalCount?: (response: TResponse) => number | undefined;
};

export type UseFetchAllPagesResult<TItem> = {
  rows: TItem[];
  isLoading: boolean;
  error: Error | null;
  totalCount: number | null;
  fetchedCount: number;
  isTruncated: boolean;
  refetch: () => void;
};

/**
 * Client-mode pagination helper that eagerly loads all pages into memory.
 *
 * Intended for small/medium datasets where server-side row model is unnecessary,
 * but one API page is not enough to represent the full result set.
 */
export function useFetchAllPages<
  TItem,
  TRequestParams extends Record<string, unknown>,
  TResponse extends PagedResponse<TItem> = PagedResponse<TItem>,
>({
  fetchPage,
  baseParams,
  pageSize = 100,
  maxRows = 2000,
  enabled = true,
  getItems,
  getTotalCount,
}: UseFetchAllPagesOptions<TItem, TRequestParams, TResponse>): UseFetchAllPagesResult<TItem> {
  const [rows, setRows] = useState<TItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<Error | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);

  const readItems = useCallback(
    (response: TResponse): TItem[] => {
      if (getItems) {
        return getItems(response);
      }
      return Array.isArray(response?.data) ? response.data : [];
    },
    [getItems]
  );

  const readTotalCount = useCallback(
    (response: TResponse): number | undefined => {
      if (getTotalCount) {
        return getTotalCount(response);
      }
      return typeof response?.hints?.count === 'number' ? response.hints.count : undefined;
    },
    [getTotalCount]
  );

  const readHasMore = useCallback((response: TResponse): boolean | undefined => {
    return typeof response?.hints?.hasMore === 'boolean' ? response.hints.hasMore : undefined;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    const requestId = ++requestIdRef.current;

    const loadAllPages = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const firstResponse = await fetchPage({
          ...baseParams,
          offset: 0,
          limit: pageSize,
        });

        if (isCancelled || requestId !== requestIdRef.current) {
          return;
        }

        const firstItems = readItems(firstResponse);
        const serverTotal = readTotalCount(firstResponse);
        const firstHasMore = readHasMore(firstResponse);
        const safeTarget =
          typeof serverTotal === 'number' ? Math.min(serverTotal, maxRows) : maxRows;

        const mergedRows: TItem[] = [...firstItems];
        let offset = firstItems.length;

        if (serverTotal === undefined && firstHasMore === false) {
          const cappedRows = mergedRows.slice(0, maxRows);
          setRows(cappedRows);
          setTotalCount(cappedRows.length);
          return;
        }

        while (mergedRows.length < safeTarget) {
          // If server does not provide total, stop once a page returns less than pageSize.
          if (serverTotal === undefined && firstItems.length < pageSize && offset === firstItems.length) {
            break;
          }

          const nextResponse = await fetchPage({
            ...baseParams,
            offset,
            limit: pageSize,
          });

          if (isCancelled || requestId !== requestIdRef.current) {
            return;
          }

          const nextItems = readItems(nextResponse);
          if (nextItems.length === 0) {
            break;
          }

          mergedRows.push(...nextItems);
          offset += nextItems.length;

          const nextHasMore = readHasMore(nextResponse);
          if (serverTotal === undefined && nextHasMore === false) {
            break;
          }

          if (serverTotal === undefined && nextItems.length < pageSize) {
            break;
          }
        }

        const cappedRows = mergedRows.slice(0, maxRows);
        setRows(cappedRows);
        setTotalCount(serverTotal ?? mergedRows.length);
      } catch (caught) {
        if (isCancelled || requestId !== requestIdRef.current) {
          return;
        }
        setRows([]);
        setTotalCount(null);
        setError(caught instanceof Error ? caught : new Error('Failed to fetch paginated data'));
      } finally {
        if (!isCancelled && requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadAllPages();

    return () => {
      isCancelled = true;
    };
  }, [baseParams, enabled, fetchPage, maxRows, pageSize, readHasMore, readItems, readTotalCount, reloadToken]);

  const refetch = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  return useMemo(
    () => ({
      rows,
      isLoading,
      error,
      totalCount,
      fetchedCount: rows.length,
      isTruncated: rows.length >= maxRows && (totalCount === null || totalCount > maxRows),
      refetch,
    }),
    [rows, isLoading, error, totalCount, maxRows, refetch]
  );
}
